import * as React from 'react';
import { ModalHeader, Title, Box } from '@zen-common/components-base';
import { assetUtils } from '@zen/common-utils';
import {
  NamingAssetStore,
  PasswordRequiredModalContent,
  WalletStore,
  NamingUtils,
} from '@zen/common-app-parts';
import { OrderConverter, DexExecutionStore } from '@zen-dex/utils';
import usePreventAction from '../../../../../../utils/usePreventAction';
import { PriceQuantityTotalTakeModalForm } from '../PriceQuantityTotalForm';
import { usePriceQuantityTotal } from '../PriceQuantityTotalForm/usePriceQuantityTotal';
import { useValidatePriceQuantityTotal } from '../modules/useValidatePriceQuantityTotal';
import ConfirmMakeTake from '../ConfirmMakeTake';
import InnerTake from './InnerTake';

export default function TakeModalProcess({
  order,
  isBuy = false,
  price: initialPrice,
  quantity: upToQuantity,
  base,
  quote,
  onCancel,
  onSuccess,
  onError,
}) {
  /** @type {[status: ('take-form'|'confirm-after-form'|'password'), setStatus: React.SetStateAction<string>]} */
  const [status, setStatus] = React.useState('take-form');
  const [formState, setFormState] = React.useState({ original: null, rounded: null });
  const { preventAction } = usePreventAction();
  const executionStore = DexExecutionStore.useStore();

  const {
    price,
    quantity,
    total,
    limitError,
    handlePriceChange,
    handleQuantityChange,
    handleTotalChange,
  } = usePriceQuantityTotal(initialPrice, upToQuantity);
  // round the amounts before validation. the contract accepts integers only
  const rounded = React.useMemo(() => {
    return OrderConverter.roundTakeAmounts({
      isBuyOrder: isBuy,
      quantity: quantity.safeValue,
      total: total.safeValue,
      order,
    });
  }, [isBuy, order, quantity.safeValue, total.safeValue]);

  const validate = useValidatePriceQuantityTotal({
    base,
    quote,
    isBuyAction: !isBuy,
  }); // buy/sell action is opposite of the original order type
  const validation = validate({
    original: {
      price: price.value,
      quantity: quantity.value,
      total: total.value,
    },
    rounded,
  });

  const handleFormCancel = () => {
    onCancel();
  };
  const handleFormContinue = () => {
    setFormState({
      original: { price: price.value, quantity: quantity.value, total: total.value },
      rounded,
    });
    setStatus('confirm-after-form');
  };

  const handleConfirmAfterFormCancel = () => {
    setStatus('take-form');
  };
  const handleConfirmAfterFormContinue = () => {
    setStatus('password');
  };

  const handlePasswordBack = () => {
    setStatus('confirm-after-form');
  };
  const handlePasswordContinue = async (password) => {
    if (preventAction() || !formState.original || !formState.rounded) return;

    try {
      await executionStore.actions.takeOrder({
        order,
        base,
        quantity: formState.rounded.quantity,
        total: formState.rounded.total,
        password,
      });
      onSuccess();
    } catch (error) {
      onError(error);
    }
  };

  const { state: naming } = NamingAssetStore.useStore();

  const {
    state: { isLoggedIn, hasPassword, balances },
  } = WalletStore.useStore();

  const shouldDisplayLogin = hasPassword && !isLoggedIn;

  const available = React.useMemo(() => {
    if (shouldDisplayLogin || !hasPassword) return;
    if (!isBuy) {
      const available = balances.find((balance) => assetUtils.isEqual(balance.asset, quote));
      return { asset: quote, amount: available ? available.amount : '0' };
    }
    const available = balances.find((balance) => assetUtils.isEqual(balance.asset, base));
    return { asset: base, amount: available ? available.amount : '0' };
  }, [balances, base, hasPassword, isBuy, quote, shouldDisplayLogin]);

  if (status === 'take-form') {
    return (
      <Box>
        <ModalHeader>
          <Title>
            Take {isBuy ? 'buy' : 'sell'} order of {NamingUtils.displayName(base, naming)}
          </Title>
          <InnerTake
            isBuy={isBuy}
            upToQuantity={upToQuantity}
            base={base}
            quote={quote}
            naming={naming}
            initialPrice={initialPrice}
          />
        </ModalHeader>
        <PriceQuantityTotalTakeModalForm
          upToQuantity={upToQuantity}
          onSubmit={handleFormContinue}
          onCancel={handleFormCancel}
          limitError={limitError}
          onPriceChange={handlePriceChange}
          onQuantityChange={handleQuantityChange}
          onTotalChange={handleTotalChange}
          price={price}
          quantity={quantity}
          total={total}
          validation={validation}
          available={available}
          isBuy={isBuy}
        />
      </Box>
    );
  }

  if (status === 'confirm-after-form') {
    return (
      <ConfirmMakeTake
        title={`Confirm taking a ${isBuy ? 'buy' : 'sell'} order`}
        asset={base}
        command="take"
        isBuy={isBuy}
        original={formState.original}
        rounded={formState.rounded}
        cancelText="Back"
        onCancel={handleConfirmAfterFormCancel}
        onConfirm={handleConfirmAfterFormContinue}
        quote={quote}
      />
    );
  }

  if (status === 'password') {
    return (
      <PasswordRequiredModalContent
        cancelText="Back"
        submitText="Take Order"
        onCancel={handlePasswordBack}
        onSuccess={handlePasswordContinue}
      />
    );
  }

  return null;
}
